/* One address book across every project. Contacts are re-typed per job today —
   this folds them together on email (falling back to name) so a person appears
   once, with everywhere they turn up. Pure derivation; nothing new is stored. */

import type { Project } from "./types";

export interface BookEntry {
  key: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  roles: string[];                                  // Client, Structural, Contractor…
  projects: { id: string; name: string; code: string }[];
}

const norm = (s?: string) => (s || "").trim().toLowerCase();

export function contactBook(projects: Project[]): BookEntry[] {
  const map = new Map<string, BookEntry>();
  const add = (o: { name?: string; email?: string; phone?: string; company?: string; role: string }, p: Project) => {
    const name = (o.name || "").trim();
    if (!name && !o.email) return;
    const key = norm(o.email) || `n:${norm(name)}`;
    const cur = map.get(key) ?? { key, name: name || o.email!, email: o.email?.trim() || undefined, phone: undefined, company: undefined, roles: [], projects: [] };
    // keep the fullest version of each field we've seen
    if (name && name.length > cur.name.length) cur.name = name;
    if (o.email && !cur.email) cur.email = o.email.trim();
    if (o.phone && !cur.phone) cur.phone = o.phone.trim();
    if (o.company && !cur.company) cur.company = o.company.trim();
    if (!cur.roles.includes(o.role)) cur.roles.push(o.role);
    if (!cur.projects.some((x) => x.id === p.id)) cur.projects.push({ id: p.id, name: p.name, code: p.code });
    map.set(key, cur);
  };

  for (const p of projects) {
    if (p.client) add({ name: p.client.name, email: p.client.email, phone: p.client.phone, company: p.client.company, role: "Client" }, p);
    for (const c of (p.client?.contacts ?? [])) add({ name: c.name, email: c.email, phone: c.phone, company: p.client?.company, role: c.role || "Client contact" }, p);
    for (const c of (p.consultants ?? [])) add({ name: c.contact || c.firm, email: c.email, phone: c.phone, company: c.firm, role: c.discipline }, p);
    for (const t of (p.partners ?? [])) add({ name: t.name, email: t.email, role: t.kind === "contractor" ? "Contractor" : "Consultant" }, p);
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/* ---- predictive programme -------------------------------------------------
   How long a stage ACTUALLY took on finished work, used to re-forecast the one
   in progress. Honest about its own evidence: with one past project it says so
   rather than dressing a guess as a forecast. */

export interface StageHistory { key: string; label: string; samples: number; medianDays: number }
export interface Forecast {
  stageKey: string; stageLabel: string;
  startedOn?: string;
  elapsedDays: number;
  typicalDays?: number;
  projectedEnd?: string;
  confidence: "none" | "low" | "fair";
  note: string;
}

const DAY = 86_400_000;
const median = (xs: number[]) => { const s = [...xs].sort((a, b) => a - b); return s.length % 2 ? s[(s.length - 1) / 2] : Math.round((s[s.length / 2 - 1] + s[s.length / 2]) / 2); };

/** Durations per stage across every project that has dated, completed stages. */
export function stageHistory(projects: Project[]): StageHistory[] {
  const buckets = new Map<string, { label: string; days: number[] }>();
  for (const p of projects) {
    const stages = ((p as any).stages ?? []) as any[];
    for (const s of stages) {
      if (s.status !== "done" || !s.startedOn || !s.completedOn) continue;
      const days = Math.round((Date.parse(s.completedOn) - Date.parse(s.startedOn)) / DAY);
      if (!(days > 0) || days > 3000) continue;                 // ignore nonsense
      const b = buckets.get(s.key) ?? { label: s.label as string, days: [] as number[] };
      b.days.push(days); buckets.set(s.key, b);
    }
  }
  return [...buckets.entries()].map(([key, b]) => ({ key, label: b.label, samples: b.days.length, medianDays: median(b.days) }));
}

/** Re-forecast the stage a project is actually in, from that history. */
export function forecastStage(p: Project, history: StageHistory[]): Forecast | null {
  const stages = ((p as any).stages ?? []) as any[];
  const cur = stages.find((s) => s.status === "active");
  if (!cur) return null;
  const started = cur.startedOn as string | undefined;
  const elapsedDays = started ? Math.max(0, Math.round((Date.now() - Date.parse(started)) / DAY)) : 0;
  const h = history.find((x) => x.key === cur.key);
  if (!h || h.samples === 0) {
    return { stageKey: cur.key, stageLabel: cur.label, startedOn: started, elapsedDays, confidence: "none",
      note: "No finished project has run this stage yet, so there's nothing to forecast from." };
  }
  const projected = started ? new Date(Date.parse(started) + h.medianDays * DAY).toISOString().slice(0, 10) : undefined;
  const confidence = h.samples >= 3 ? "fair" : "low";
  const over = elapsedDays - h.medianDays;
  const note = confidence === "low"
    ? `Based on ${h.samples} past project${h.samples === 1 ? "" : "s"} — too few to be confident, treat it as a rough marker.`
    : over > 0
      ? `Typically ${h.medianDays} days; this one is ${over} day${over === 1 ? "" : "s"} past that.`
      : `Typically ${h.medianDays} days; ${Math.abs(over)} day${Math.abs(over) === 1 ? "" : "s"} of that still to run.`;
  return { stageKey: cur.key, stageLabel: cur.label, startedOn: started, elapsedDays, typicalDays: h.medianDays, projectedEnd: projected, confidence, note };
}
